import { useNavigate } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';

import { NoSavedQueries } from './no-saved-queries';
import { SavedQueryCard } from './card';
import { SavedQueriesHeader } from './header';
import { UpgradePromotionCard } from './upgrade-promotion-card';

import { WORKSPACE_PLAN } from '@/entities/workspace/workspace-constants';
import { PageLoader } from '@/shared/components/loader/page-loader';
import { Button } from '@/shared/components/shadcn/button';
import { useSavedQueries } from '@/shared/hooks/use-saved-queries';
import { useWorkspace } from '@/shared/hooks/use-workspace';

export function SavedQueriesPage() {
  const navigate = useNavigate();
  const { workspace } = useWorkspace();
  const workspaceId = workspace.id;
  const { data: savedQueries, isLoading, error } = useSavedQueries({ workspaceId });

  const hasPrivateQuery = savedQueries?.some((query) => query.type === 'private');

  if (isLoading || savedQueries === undefined) {
    return <PageLoader />;
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-red-600">Error loading saved queries</h3>
          <p className="text-sm text-zinc-500">Please try again later</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <SavedQueriesHeader />

      <div className="flex-1 overflow-auto">
        {savedQueries.length === 0 ? (
          <NoSavedQueries />
        ) : (
          <div className="relative box-border flex flex-col gap-3 p-6">
            {workspace?.plan === WORKSPACE_PLAN.TRIAL &&
              (hasPrivateQuery ? (
                <UpgradePromotionCard
                  title="Upgrade to discover more"
                  description="Upgrade your plan to explore more data and gain deeper insights"
                  actions={
                    <>
                      <Button
                        variant="secondary"
                        onClick={() => {
                          navigate({
                            to: '/workspace/$workspaceId/settings/plan',
                            params: { workspaceId },
                          });
                        }}
                      >
                        View plans
                        <ArrowRight className="size-4" />
                      </Button>
                    </>
                  }
                />
              ) : (
                <UpgradePromotionCard
                  title="Create your first listening"
                  description="Explore real insights with one free listening during your trial"
                  actions={
                    <>
                      <Button
                        className="bg-transparent text-white"
                        onClick={() => {
                          navigate({
                            to: '/workspace/$workspaceId/settings/plan',
                            params: { workspaceId },
                          });
                        }}
                      >
                        View plans
                      </Button>
                      <Button
                        variant="secondary"
                        className="bg-background text-foreground hover:bg-background/90"
                        onClick={() =>
                          navigate({
                            to: '/workspace/$workspaceId/create-new',
                            params: { workspaceId },
                          })
                        }
                      >
                        Create now
                      </Button>
                    </>
                  }
                />
              ))}
            {savedQueries.map((query) => (
              <SavedQueryCard key={query.id} savedQuery={query} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
